import { Directive, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  finalize,
  Subject,
  switchMap,
  takeUntil
} from 'rxjs';

import { MonitoringService } from '@core/services/monitoring.service';
import { PageRequest } from '@shared/components/data-table/models/page-request';
import { Report } from '@shared/enums/report.enum';
import { CSVField, downloadInCsv } from '@shared/utilities/json-util';

import { ReportsService } from '../services/reports.service';

@Directive()
export abstract class ReportAbstract<T, U = unknown> implements OnDestroy {
  abstract csvHeaders: CSVField[];
  abstract title: string;
  downloading = false;
  filter$ = new BehaviorSubject<U>(this.defaultFilter);
  private destroying$ = new Subject<null>();

  nextPage$ = new BehaviorSubject<PageRequest>({ page: 1, size: 20 });

  data$ = combineLatest([this.filter$, this.nextPage$]).pipe(
    switchMap(([f, p]) =>
      this.reportsService.getSummaryWithPagination<T>(this.report, {
        ...f,
        ...p
      })
    )
  );

  constructor(
    protected readonly report: Report,
    protected readonly reportsService: ReportsService,
    protected readonly monitoringService: MonitoringService,
    protected readonly defaultFilter = {} as unknown as U
  ) {}

  ngOnDestroy(): void {
    this.destroying$.next(null);
    this.destroying$.complete();
  }

  pageChanged(page: number, size: number) {
    this.nextPage$.next({ page, size });
  }

  filterChange(filter: U) {
    this.filter$.next(filter);
  }

  downloadClicked() {
    this.downloading = true;
    this.reportsService
      .getDetails<T>(this.report)
      .pipe(
        takeUntil(this.destroying$),
        finalize(() => (this.downloading = false))
      )
      .subscribe((d) => {
        downloadInCsv('download.csv', d.data, this.csvHeaders);
        this.monitoringService.logEvent(this.title, {
          action: 'download',
          view: 'reports'
        });
      });
  }
}
